import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { currencyApi } from '@/lib/api/currency';
import { findCurrency } from '@/app/components/venue-form/currency-data';

interface ViewerCurrencyState {
  /** Viewer's preferred display currency (ISO-4217). Null until detected/chosen. */
  currencyCode: string | null;
  /** True if `currencyCode` came from IP auto-detection rather than an explicit user choice. */
  isAutoDetected: boolean;
  rates: Record<string, number> | null;
  ratesBase: string | null;
  hasInitialized: boolean;

  /** Runs once per app load: fetches rates, and if the viewer hasn't picked a currency, detects one from IP. */
  init: () => Promise<void>;
  /** Explicit user choice from the currency picker — persists and stops auto-detection from overriding it. */
  setCurrency: (code: string) => void;

  /** Convert an amount priced in `fromCode` into the viewer's chosen currency. Returns the original amount if rates/currency aren't ready. */
  convert: (amount: number, fromCode?: string | null) => number;
  /** Symbol for the viewer's current display currency (falls back to "$"). */
  symbol: () => string;
}

export const useViewerCurrencyStore = create<ViewerCurrencyState>()(
  persist(
    (set, get) => ({
      currencyCode: null,
      isAutoDetected: false,
      rates: null,
      ratesBase: null,
      hasInitialized: false,

      init: async () => {
        if (get().hasInitialized) return;
        set({ hasInitialized: true });

        try {
          const { base, rates } = await currencyApi.getRates();
          set({ rates, ratesBase: base });
        } catch {
          // Conversion just won't be available this session — display falls back to venue's own currency.
        }

        if (!get().currencyCode) {
          try {
            const { currency } = await currencyApi.locate();
            if (currency && findCurrency(currency.toUpperCase())) {
              set({ currencyCode: currency.toUpperCase(), isAutoDetected: true });
            }
          } catch {
            // No geolocation available — viewer sees venue-native prices until they pick a currency.
          }
        }
      },

      setCurrency: (code: string) => {
        set({ currencyCode: code.toUpperCase(), isAutoDetected: false });
      },

      convert: (amount, fromCode) => {
        const { rates, currencyCode } = get();
        const from = (fromCode || 'USD').toUpperCase();
        if (!rates || !currencyCode || currencyCode === from) return amount;
        const fromRate = rates[from];
        const toRate = rates[currencyCode];
        if (!fromRate || !toRate) return amount;
        return (amount / fromRate) * toRate;
      },

      symbol: () => {
        const { currencyCode } = get();
        if (!currencyCode) return '$';
        return findCurrency(currencyCode)?.symbol ?? '$';
      },
    }),
    {
      name: 'venuelista_viewer_currency',
      partialize: (state) => ({
        currencyCode: state.currencyCode,
        isAutoDetected: state.isAutoDetected,
      }),
    }
  )
);
